<template>
    <b-modal 
        id="add-new-membership-category-sidebar" 
        :visible="isAddNewmembershipSidebarActive" 
        bg-variant="white"
        title="Add New Membership Parameter" 
        size="xl" 
        @hidden="closeSidebar" 
        hide-footer
        @change="(val) => $emit('update:is-add-new-membership-category-sidebar-active', val)">
        <template #default="{ hide }">
            <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
                <b-form class="p-3" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
                    <b-alert v-if="formError" show variant="danger" class="mb-3">{{ formError }}</b-alert>
                    <!-- Sección: Información Básica -->
                    <b-card class="mb-3 border-0 shadow-sm" no-body>
                        <b-card-header class="bg-light border-bottom">
                            <h6 class="mb-0 text-primary">
                                <i class="fas fa-info-circle mr-2"></i>
                                Basic Information
                            </h6>
                        </b-card-header>
                        <b-card-body>
                            <validation-provider #default="validationContext" name="name" rules="required">
                                <b-form-group label="Name" label-for="name">
                                    <b-form-input 
                                        id="name" 
                                        v-model="stateData.name" 
                                        autofocus
                                        :state="getValidationState(validationContext)" 
                                        trim
                                        placeholder="Enter membership parameter name" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <b-row>
                                <b-col cols="12" md="4">
                                    <validation-provider #default="validationContext" name="validity" rules="required|numeric|max_value:12">
                                        <b-form-group label="Validity (months)" label-for="validity">
                                            <b-form-input 
                                                id="validity" 
                                                v-model="stateData.validity"
                                                :state="getValidationState(validationContext)" 
                                                trim 
                                                type="number"
                                                min="1"
                                                max="12"
                                                placeholder="1-12 months" />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <validation-provider #default="validationContext" name="membership_type" rules="required">
                                        <b-form-group label="Membership Type" label-for="membership_type">
                                            <treeselect 
                                                id="membership_type" 
                                                v-model="stateData.membership_type"
                                                :options="membershipTypeOptions" 
                                                :clearable="false" />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <validation-provider #default="validationContext" name="group_membership" rules="required">
                                        <b-form-group label="Group Membership" label-for="group_membership">
                                            <treeselect 
                                                id="group_membership" 
                                                v-model="stateData.group_membership"
                                                :options="groupMembershipOptions" 
                                                :clearable="false" />
                                            <b-form-invalid-feedback>
                                                {{ validationContext.errors[0] }}
                                            </b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </b-col>
                            </b-row>

                            <validation-provider #default="validationContext" name="price" rules="required">
                                <b-form-group label="Price" label-for="price">
                                    <b-form-input 
                                        id="price" 
                                        v-model="stateData.price"
                                        :state="getValidationState(validationContext)" 
                                        trim 
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="Enter price" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <b-row>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Quarter price multiplier" label-for="add-quarter-multiplier" description="Vacío = no se ofrece trimestral. Trimestral = mensual × este valor.">
                                        <b-form-input id="add-quarter-multiplier" v-model="stateData.quarter_price_multiplier" type="number" min="0" step="0.01" placeholder="2.75" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="6">
                                    <b-form-group label="Year price multiplier" label-for="add-year-multiplier" description="Anual = mensual × este valor.">
                                        <b-form-input id="add-year-multiplier" v-model="stateData.year_price_multiplier" type="number" min="0" step="0.01" placeholder="10" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Enrollment fee" label-for="add-enrollment-fee">
                                        <b-form-input id="add-enrollment-fee" v-model="stateData.enrollment_fee" type="number" min="0" step="0.01" placeholder="0" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Reactivation fee" label-for="add-reactivation-fee">
                                        <b-form-input id="add-reactivation-fee" v-model="stateData.reactivation_fee" type="number" min="0" step="0.01" placeholder="25" />
                                    </b-form-group>
                                </b-col>
                                <b-col cols="12" md="4">
                                    <b-form-group label="Uncovered discount %" label-for="add-uncovered-discount" description="Vacío = el backend usa 20 en Tu Salud y 30 en los demás.">
                                        <b-form-input id="add-uncovered-discount" v-model="stateData.uncovered_discount_percentage" type="number" min="0" max="100" step="1" />
                                    </b-form-group>
                                </b-col>
                            </b-row>

                            <b-row class="mt-2">
                                <b-col cols="12" md="4">
                                    <b-form-group label="Plan kind" label-for="add-plan-kind" description="Familiar usa tramos por cantidad de personas.">
                                        <b-form-select id="add-plan-kind" v-model="stateData.plan_kind" :options="planKindOptions" />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <template v-if="stateData.plan_kind === 'family'">
                                <b-row>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Minimum members" label-for="add-min-members">
                                            <b-form-input id="add-min-members" v-model="stateData.min_members" type="number" min="2" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Child minimum age" label-for="add-child-age">
                                            <b-form-input id="add-child-age" v-model="stateData.child_min_age" type="number" min="0" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Adult minimum age" label-for="add-adult-age">
                                            <b-form-input id="add-adult-age" v-model="stateData.adult_min_age" type="number" min="0" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col cols="12" md="3">
                                        <b-form-group label="Extra member amount" label-for="add-extra-member">
                                            <b-form-input id="add-extra-member" v-model="stateData.extra_member_amount" type="number" min="0" step="0.01" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span class="text-primary">Family tiers</span>
                                        <b-button size="sm" variant="outline-primary" type="button" @click="addFamilyTier">Add tier</b-button>
                                    </div>
                                    <b-row v-for="(tier, tierIndex) in stateData.family_tiers" :key="'add-tier-' + tierIndex" class="mb-2">
                                        <b-col cols="12" md="4">
                                            <b-form-input v-model="tier.members" type="number" min="2" placeholder="Members" />
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-form-input v-model="tier.amount" type="number" min="0" step="0.01" placeholder="Amount" />
                                        </b-col>
                                        <b-col cols="12" md="4">
                                            <b-button size="sm" variant="outline-danger" type="button" @click="removeFamilyTier(tierIndex)">Remove</b-button>
                                        </b-col>
                                    </b-row>
                                </div>
                            </template>
                        </b-card-body>
                    </b-card>

                    <!-- Sección: Configuración -->
                    <b-card class="mb-3 border-0 shadow-sm" no-body>
                        <b-card-header class="bg-light border-bottom">
                            <h6 class="mb-0 text-primary">
                                <i class="fas fa-cog mr-2"></i>
                                Configuration
                            </h6>
                        </b-card-header>
                        <b-card-body>
                            <validation-provider #default="validationContext" name="facility" rules="required">
                                <b-form-group label="Facilities" label-for="facility">
                                    <treeselect 
                                        id="facility" 
                                        v-model="facilities" 
                                        :options="facilityOptionsWithSelectAll" 
                                        :multiple="true"
                                        :clearable="true" 
                                        :normalizer="normalizerFacilities" 
                                        @input="handleFacilityChange" />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <!-- Group Membership Fields -->
                            <template v-if="stateData.group_membership === true">
                                <b-row>
                                    <b-col cols="12" md="6">
                                        <validation-provider #default="validationContext" name="adults_count" :rules="stateData.group_membership ? 'required|numeric|min_value:0' : ''">
                                            <b-form-group label="Adults" label-for="adults_count">
                                                <b-form-input 
                                                    id="adults_count" 
                                                    v-model="stateData.adults_count"
                                                    :state="getValidationState(validationContext)" 
                                                    trim 
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter number of adults" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>
                                    <b-col cols="12" md="6">
                                        <validation-provider #default="validationContext" name="minors_count" :rules="stateData.group_membership ? 'required|numeric|min_value:0' : ''">
                                            <b-form-group label="Minors" label-for="minors_count">
                                                <b-form-input 
                                                    id="minors_count" 
                                                    v-model="stateData.minors_count"
                                                    :state="getValidationState(validationContext)" 
                                                    trim 
                                                    type="number"
                                                    min="0"
                                                    placeholder="Enter number of minors" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>
                                </b-row>
                            </template>
                        </b-card-body>
                    </b-card>

                    <!-- Sección: Parámetros -->
                    <b-card class="mb-3 border-0 shadow-sm" no-body>
                        <b-card-header class="bg-light border-bottom d-flex justify-content-between align-items-center">
                            <h6 class="mb-0 text-primary">
                                <i class="fas fa-list-ul mr-2"></i>
                                Membership Parameters
                            </h6>
                            <small class="text-muted">{{ parameters.length }} parameter(s)</small>
                        </b-card-header>
                        <b-card-body>
                            <div 
                                v-for="(parameter, index) in parameters" 
                                :key="index"
                                class="parameter-item mb-3 p-3 border rounded"
                                :class="{ 'bg-light': index % 2 === 0 }">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="mb-0 text-muted">Parameter #{{ index + 1 }}</h6>
                                    <b-button 
                                        v-if="index !== 0"
                                        variant="outline-danger" 
                                        size="sm"
                                        @click="removeParameter(index)"
                                        class="parameter-remove-btn">
                                        <i class="fas fa-trash-alt"></i>
                                        <span class="ml-1 d-none d-sm-inline">Delete</span>
                                    </b-button>
                                </div>
                                
                                <b-row>
                                    <b-col cols="12" md="6" lg="3">
                                        <validation-provider #default="validationContext" :name="`parameter_type_${index}`" rules="required">
                                            <b-form-group label="Parameter Type" :label-for="`parameter_type_${index}`">
                                                <treeselect 
                                                    v-model="parameter.type" 
                                                    :async="true" 
                                                    :options="options"
                                                    :defaultOptions="true" 
                                                    :load-options="searchMemberships" 
                                                    search-nested
                                                    :disable-branch-nodes="true" 
                                                    valueFormat="object"
                                                    placeholder="Select type" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="2">
                                        <validation-provider #default="validationContext" :name="`count_${index}`" rules="required">
                                            <b-form-group label="Quantity" :label-for="`count_${index}`">
                                                <b-form-input 
                                                    v-model="parameter.count" 
                                                    :state="getValidationState(validationContext)"
                                                    trim 
                                                    type="number"
                                                    min="1"
                                                    placeholder="Quantity" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="2">
                                        <validation-provider #default="validationContext" :name="`activated_at_${index}`" rules="required">
                                            <b-form-group label="Activated at (month)" :label-for="`activated_at_${index}`">
                                                <treeselect 
                                                    v-model="parameter.activated_at"
                                                    :disabled="!stateData.validity || stateData.validity === null"
                                                    :options="months"
                                                    placeholder="Month" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] }}
                                                </b-form-invalid-feedback>
                                                <small v-if="!stateData.validity || stateData.validity === null" class="text-muted">
                                                    First define validity
                                                </small>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="3">
                                        <validation-provider #default="validationContext" :name="`discount_percentage_${index}`" :rules="{ min_value: 0, max_value: 100, numeric: true }">
                                            <b-form-group label="Discount (%)" :label-for="`discount_percentage_${index}`">
                                                <b-form-input 
                                                    v-model.number="parameter.discount_percentage" 
                                                    :state="getValidationState(validationContext)"
                                                    trim 
                                                    type="number" 
                                                    min="0" 
                                                    max="100" 
                                                    step="0.01"
                                                    placeholder="0-100" />
                                                <b-form-invalid-feedback>
                                                    {{ validationContext.errors[0] || 'Percentage must be between 0 and 100' }}
                                                </b-form-invalid-feedback>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-col>

                                    <b-col cols="12" md="6" lg="2">
                                        <b-form-group label="Audience" :label-for="`member_type_${index}`" description="Vacío = adulto y niño.">
                                            <b-form-select :id="`member_type_${index}`" v-model="parameter.member_type" :options="benefitAudienceOptions" />
                                        </b-form-group>
                                    </b-col>

                                    <b-col cols="12" lg="2" class="d-flex align-items-end">
                                        <b-button 
                                            v-if="index === parameters.length - 1"
                                            variant="outline-primary" 
                                            size="sm"
                                            @click="addParameter"
                                            class="w-100 parameter-add-btn">
                                            <i class="fas fa-plus mr-1"></i>
                                            Add
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-card-body>
                    </b-card>

                    <!-- Form Actions -->
                    <div class="d-flex justify-content-end mt-4 pt-3 border-top">
                        <b-button 
                            type="button" 
                            variant="outline-secondary" 
                            class="mr-2"
                            @click="hide"
                            :disabled="loading">
                            {{ $t("Cancel") }}
                        </b-button>
                        <b-button 
                            variant="primary" 
                            type="submit" 
                            :disabled="loading">
                            <span v-if="!loading">
                                <i class="fas fa-save mr-1"></i>
                                {{ $t("Add") }}
                            </span>
                            <span v-else>
                                <SpinnerLoading />
                            </span>
                        </b-button>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </b-modal>
</template>

<script>
import {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BAlert,
} from "bootstrap-vue";
import formValidation from "@/core/utils/forms/form-validation";
import { LOAD_CHILDREN_OPTIONS } from '@riophae/vue-treeselect'
import { ValidationProvider, ValidationObserver } from "vee-validate";
import SpinnerLoading from "@/components/SpinnerLoading";

import { required } from "@validations";
import { ref, watch, computed } from "@vue/composition-api";

import axiosCT from "@/core/services/api/admin/consults/memberships";
import { applyFamilyPlanFields, applyMembershipPricingFields, BENEFIT_AUDIENCE_OPTIONS, membershipApiMessage, PLAN_KIND_OPTIONS } from "@/core/utils/membershipAdmin";

/**
 * Component for adding new membership parameters.
 * Allows configuring basic information, facilities and dynamic parameters.
 */

export default {
    components: {
        BModal,
        BForm,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,
        BButton,
        BRow,
        BCol,
        BCard,
        BCardHeader,
        BCardBody,
        BAlert,
        SpinnerLoading,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },
    model: {
        prop: "isAddNewmembershipSidebarActive",
        event: "update:is-add-new-membership-category-sidebar-active",
    },
    props: {
        isAddNewmembershipSidebarActive: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            required,
            normalizerFacilities(node) {
                return {
                    id: node.id,
                    label: node.name,
                };
            },
            normalizer(node) {
                return {
                    id: node.id,
                    label: node.name,
                    children: []
                };
            },
        };
    },
    setup(props, { emit }) {
        // Estado del formulario
        const facilities = ref([])
        const parameters = ref([{ type: null, count: null, activated_at: null, discount_percentage: 0, member_type: '' }])
        const months = ref([])
        const loading = ref(false)
        const formError = ref(null)
        
        // Opciones para búsqueda de parámetros
        const parameterTypeOptions = ref([])
        const laboratories = ref([])
        const laboratoryCategories = ref([])
        const procedures = ref([])
        const sonograms = ref([])
        const tests = ref([])
        const treatments = ref([])
        
        // Opciones de instalaciones
        const facilityOptions = JSON.parse(atob(localStorage.getItem("setFacilityOptions")));
        const SELECT_ALL_ID = "select-all-facilities";
        
        const facilityOptionsWithSelectAll = computed(() => {
            const selectAllOption = {
                id: SELECT_ALL_ID,
                name: "Select All",
            };
            return [selectAllOption, ...facilityOptions];
        });

        const facilityOptionIds = () =>
            facilityOptions
                .map(option => option.id)
                .filter(id => id !== undefined && id !== null);

        const handleFacilityChange = (selectedValues) => {
            if (Array.isArray(selectedValues)) {
                const hasSelectAll = selectedValues.includes(SELECT_ALL_ID);
                const ids = facilityOptionIds();
                
                if (hasSelectAll) {
                    // Si se seleccionó "Seleccionar todo", seleccionar solo todas las facilidades (sin el SELECT_ALL_ID)
                    facilities.value = ids;
                } else {
                    // Si se deseleccionó "Seleccionar todo" o se deseleccionó alguna facilidad
                    facilities.value = selectedValues.filter(val => val !== SELECT_ALL_ID);
                }
            } else {
                if (selectedValues === SELECT_ALL_ID) {
                    facilities.value = facilityOptionIds();
                } else {
                    facilities.value = [selectedValues];
                }
            }
        };

        const optionsStatus = [
            { name: "Active", id: true },
            { name: "Inactive", id: false },
        ];
        const membershipTypeOptions = [
            { id: 0, label: "Membership" },
            { id: 1, label: "Medical Plan" },
        ];
        const groupMembershipOptions = [
            { id: false, label: "Individual" },
            { id: true, label: "Group" },
        ];
        const stateDataTem = {
            name: null,
            price: null,
            validity: null,
            membership_type: 0,
            group_membership: false,
            status: true,
            adults_count: 0,
            minors_count: 0,
            quarter_price_multiplier: 2.75,
            year_price_multiplier: 10,
            enrollment_fee: 0,
            reactivation_fee: 25,
            uncovered_discount_percentage: null,
            plan_kind: 'individual',
            min_members: 2,
            child_min_age: 2,
            adult_min_age: 18,
            extra_member_amount: null,
            family_tiers: [],
        };

        /**
         * Transforms an array of data into tree structure for treeselect.
         * @param {Object} params - Transformation parameters
         * @param {Array} params.array - Array of elements to transform
         * @param {string} params.subChildren - Name of the property containing sub-elements
         * @param {number|string} params.parentId - Parent element ID
         * @returns {Array} Transformed array for treeselect
         */
        const transformToTreeSelectOptions = ({ array, subChildren, parentId }) => {
            return array.map((child) => {
                if (subChildren && child[subChildren]) {
                    const subChildrenOptions = child[subChildren].map(subChild => ({
                        id: `${subChild.id} - ${subChild.name} - ${parentId}`,
                        label: subChild.price ? `${subChild.name} - ${subChild.price}` : subChild.name,
                        trueId: subChild.id,
                        fatherId: parentId
                    }));
                    
                    return {
                        id: `${child.id} - ${child.name} - ${parentId}`,
                        label: child.name,
                        children: subChildrenOptions,
                        trueId: child.id,
                        fatherId: parentId
                    };
                }
                
                return {
                    id: `${child.id} - ${child.name} - ${parentId}`,
                    label: child.price ? `${child.name} - ${child.price}` : child.name,
                    trueId: child.id,
                    fatherId: parentId
                };
            });
        }

        /**
         * Searches and loads parameter type options for treeselect.
         * Handles asynchronous search and child loading by type.
         */
        const searchMemberships = ({ action, searchQuery, callback, parentNode }) => {
            if (action === "ASYNC_SEARCH") {
                axiosCT.typeParameterSearch()
                    .then(({ data: { data } }) => {
                        const transformedData = data.map((res) => {
                            if (res.identifier === "consultations") {
                                return {
                                    id: res.id,
                                    label: res.name,
                                    identifier: res.identifier,
                                };
                            }
                            return {
                                id: res.id,
                                label: res.name,
                                identifier: res.identifier,
                                children: null
                            };
                        });
                        parameterTypeOptions.value = transformedData;
                        callback(null, transformedData);
                    })
                    .catch((error) => {
                        console.error('Error al buscar tipos de parámetros:', error);
                        callback(error);
                    });
                return;
            }

            if (action === LOAD_CHILDREN_OPTIONS) {
                try {
                    const identifierMap = {
                        "procedures": { array: procedures.value },
                        "laboratories": { array: laboratories.value, subChildren: "laboratories" },
                        "laboratories_pack": { array: laboratoryCategories.value },
                        "test": { array: tests.value },
                        "diagnostic_imagings": { array: sonograms.value, subChildren: "diagnostic_imagings" },
                        "treatments": { array: treatments.value }
                    };

                    const config = identifierMap[parentNode.identifier];
                    if (!config) {
                        callback(new Error('Tipo de parámetro no encontrado'));
                        return;
                    }

                    parentNode.children = transformToTreeSelectOptions({
                        array: config.array,
                        subChildren: config.subChildren,
                        parentId: parentNode.id
                    });
                    callback();
                } catch (error) {
                    console.error('Error al cargar opciones hijas:', error);
                    callback(new Error('Error al cargar opciones hijas'));
                }
            }
        }

        /**
         * Loads parameterizable data from the backend.
         * @param {number} page - Page number (default 1)
         */
        const loadParametrizables = (page = 1) => {
            axiosCT.mptParametrizables(page)
                .then(({ 
                    category_lab, 
                    procedures: { data: procedure_data }, 
                    sonograms: sonogramsData, 
                    tests: { data: test_data }, 
                    treatments: treatmentsData 
                }) => {
                    laboratories.value = category_lab;
                    laboratoryCategories.value = category_lab.filter(lab => lab.price);
                    procedures.value = procedure_data;
                    sonograms.value = sonogramsData;
                    tests.value = test_data;
                    treatments.value = treatmentsData;
                })
                .catch((error) => {
                    console.error('Error al cargar parametrizables:', error);
                });
        }

        loadParametrizables();
        const stateData = ref(JSON.parse(JSON.stringify(stateDataTem)));

        const resetuserData = () => {
            stateData.value = JSON.parse(JSON.stringify(stateDataTem));
        };
        const closeSidebar = () => {
            emit("createmembership", { status: false });
        }
        const { refFormObserver, getValidationState, resetForm } =
            formValidation(resetuserData);

        /**
         * Generates month options based on parameter validity.
         * Updates the array of available months for selection.
         */
        const updateMonthsOptions = () => {
            const validity = stateData.value.validity;
            if (!validity || validity > 12 || validity < 1) {
                months.value = [];
                return;
            }
            
            months.value = Array.from({ length: validity }, (_, i) => ({
                id: `${i}`,
                label: `Month ${i + 1}`
            }));
        }

        // Observar cambios en la validez para actualizar meses
        watch(() => stateData.value.validity, () => {
            updateMonthsOptions();
        });

        // Inicializar meses al cargar
        updateMonthsOptions();

        /**
         * Adds a new parameter to the form.
         */
        const addFamilyTier = () => {
            if (!Array.isArray(stateData.value.family_tiers)) stateData.value.family_tiers = [];
            stateData.value.family_tiers.push({ members: null, amount: null });
        };
        const removeFamilyTier = (index) => {
            stateData.value.family_tiers.splice(index, 1);
        };

        const addParameter = () => {
            parameters.value.push({
                type: null,
                count: null,
                activated_at: null,
                discount_percentage: 0,
                member_type: ''
            });
        };

        /**
         * Removes a parameter from the form.
         * @param {number} index - Index of the parameter to remove
         */
        const removeParameter = (index) => {
            if (parameters.value.length > 1 && index > 0) {
                parameters.value.splice(index, 1);
            }
        };
        /**
         * Transforms form parameters to the format expected by the backend.
         * @param {Array} params - Array of form parameters
         * @returns {Array} Transformed array for the backend
         */
        const transformParametersForSubmit = (params) => {
            return params.map(({ count, activated_at, discount_percentage, member_type, type }) => {
                if (!type || !type.id) {
                    throw new Error('All parameters must have a selected type');
                }

                const { id, trueId, fatherId } = type;
                return {
                    count,
                    activated_at,
                    discount_percentage: discount_percentage ? Number(discount_percentage) : 0,
                    member_type: member_type || null,
                    parametrizable_id: trueId,
                    memberships_parameters_types_id: id === 1 ? id : fatherId
                };
            });
        };

        /**
         * Generates the parameters_groups array based on adults and minors counts.
         * @param {number} adultsCount - Number of adults
         * @param {number} minorsCount - Number of minors
         * @returns {Array} Array of parameters_groups
         */
        const generateParametersGroups = (adultsCount, minorsCount) => {
            const groups = [];
            
            // Add adults (type: 1)
            for (let i = 0; i < adultsCount; i++) {
                groups.push({ type: 1 });
            }
            
            // Add minors (type: 2)
            for (let i = 0; i < minorsCount; i++) {
                groups.push({ type: 2 });
            }
            
            return groups;
        };

        /**
         * Handles form submission.
         * Validates, transforms and sends data to the backend.
         */
        const onSubmit = () => {
            loading.value = true;
            formError.value = null;
            
            try {
                const transformedParameters = transformParametersForSubmit(parameters.value);
                const formData = applyFamilyPlanFields(applyMembershipPricingFields({
                    ...stateData.value,
                    parameters_details: transformedParameters,
                    facilities: facilities.value,
                }));

                // If group membership, add parameters_groups
                if (formData.group_membership === true) {
                    const adultsCount = Number(formData.adults_count) || 0;
                    const minorsCount = Number(formData.minors_count) || 0;
                    formData.parameters_groups = generateParametersGroups(adultsCount, minorsCount);
                }

                // Remove auxiliary fields before sending
                delete formData.adults_count;
                delete formData.minors_count;

                axiosCT.membershipParameterCreate(formData)
                    .then(({ registro }) => {
                        loading.value = false;
                        resetuserData();
                        emit("createmembership", { status: true, registro });
                    })
                    .catch((error) => {
                        console.error('Error creating membership parameter:', error);
                        formError.value = membershipApiMessage(error, 'No se pudo crear el plan.');
                        loading.value = false;
                    });
            } catch (error) {
                console.error('Error transforming parameters:', error);
                formError.value = error && error.message ? error.message : 'No se pudo crear el plan.';
                loading.value = false;
            }
        };
        return {
            // Estado
            loading,
            formError,
            stateData,
            facilities,
            parameters,
            months,
            
            // Opciones
            optionsStatus,
            membershipTypeOptions,
            groupMembershipOptions,
            planKindOptions: PLAN_KIND_OPTIONS,
            benefitAudienceOptions: BENEFIT_AUDIENCE_OPTIONS,
            facilityOptions,
            facilityOptionsWithSelectAll,
            options: parameterTypeOptions,
            
            // Métodos
            onSubmit,
            addParameter,
            removeParameter,
            addFamilyTier,
            removeFamilyTier,
            searchMemberships,
            handleFacilityChange,
            closeSidebar,
            
            // Validación
            refFormObserver,
            getValidationState,
            resetForm,
        };
    },
};
</script>

<style scoped>
.parameter-item {
    transition: all 0.2s ease-in-out;
}

.parameter-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.parameter-remove-btn,
.parameter-add-btn {
    transition: all 0.2s ease-in-out;
}

.parameter-remove-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
}

.parameter-add-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.bg-light {
    background-color: #f8f9fa !important;
}

.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}
</style>
